import Core       from "@/core/Core";
import FeedRecord  from '@/core/services/Feeds/FeedRecord';

import ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';
import ProductionPlansDAO from "@/domains/ProductionPlans/ProductionPlansDAO";
import ProductionPlan from "@/domains/ProductionPlans/ProductionPlan";
import RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import ProductionPlanProduct from "@/domains/ProductionPlans/ProductionPlanProduct";
import Utils from "@/core/utils/Utils";

class ProductionPlansService {

  static defaultPath = '/admin/production/current';

  static productionSiteOptions = [
    {
      id: 'aromapolis',
      name: 'Аромаполис'
    },
    {
      id: 'berdsk',
      name: 'Бердск'
    },
    {
      id: 'naturofarm',
      name: 'Натурофарм'
    },
    {
      id: 'sw_alt_bioproduct',
      name: 'Алт-Биопродукт'
    }
  ];

  static tabs = [
    {
      id:   'current',
      name: 'Текущий'
    },
  ];

  static responseDataTabs = [
    {
      id:   'all',
      name: 'Все'
    },
    {
      id:   'added',
      name: 'Добавленные'
    },
    {
      id:   'updated',
      name: 'Обновленные'
    },
    {
      id:   'deleted',
      name: 'Удаленные'
    },
  ];

  static requiredFields = ['Код', 'Наименование', 'Площадка'];

  static months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
  ];

  /** номера для преобразования даты */
  static monthDigits = [
    '01', '02', '03', '04', '05', '06',
    '07', '08', '09', '10', '11', '12',
  ];

  /**
   * Доменное контекстное меню позиции произв. плана.
   * Рендерится гридом (кебаб-колонка + правый клик) через :context-menu.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Добавить позицию',
        'add',
        (row) => { return ProductionPlansService.showAddPositionDialog(Object.keys(row.qtyByDates).sort()); },
      ),
      new ContextMenuItem(
        'История товара',
        'history',
        (row) => { ProductionPlansService.showProductHistoryDialog(row); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (row) => { return ProductionPlansService.confirmAndDeleteProduct(row); },
      ),
    ];
  }

  /** Открывает диалог добавления позиции. Промис: true — позицию добавили, false — закрыли. */
  static showAddPositionDialog(monthKeys): Promise<boolean> {
    return Core.dialogs.AddProductionPositionPopup.showAsPromise({ monthKeys });
  }

  /** Диалог истории товара (все месяцы). */
  static showProductHistoryDialog(position) {
    Core.dialogs.FeedDialog.show({
      entityTypeName:  'productionPlanProduct',
      showAddComment:  false,
      showDownloadBtn: true,
      title:           `${position.name}`,
      productCode:     position.code,
    });
  }

  /** Удаление позиции с подтверждением. Возвращает true, если позиция удалена. */
  static async confirmAndDeleteProduct(position): Promise<boolean> {
    const isConfirmed = await Core.confirm('Подтверждаете удаление?');

    if (isConfirmed === true) {
      return await ProductionPlansService.delete({ productCode: position.code, productionSite: { id: position.productionSite.id } });
    }
    else {
      return false;
    }
  }

  static redirect(to, from, next) {
    next({ path: ProductionPlansService.defaultPath });
  }

  static ensureValidTab(to, from, next) {
    const { registry } = to.params;

    if (ProductionPlansService.tabs.find(tab => tab.id === registry)) {
      next();
    } else {
      next({ path: ProductionPlansService.defaultPath });
    }
  }

  static formatMonthLabel(monthKey) {
    /** взять из report, а лучше сделать единый пул для такого рода данных */
    const months = [
      'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    const year       = monthKey.substring(0, 4);
    const monthIndex = parseInt(monthKey.substring(5, 7), 10) - 1;

    return {
      month: months[monthIndex],
      year,
    };
  }

  static fromNumFormat(date) {
    return `${date.slice(0, 4)}-${date.slice(5, 7)}-${date.slice(6)}`;
  }

  static getProductCodesFromDiffs(diffs) {
    const codes = new Set();
    const result = [];

    diffs.forEach((planDiff) => {
      planDiff.productsDiff.added.forEach((row) => {
        codes.add(row.code);
      })

      planDiff.productsDiff.deleted.forEach((row) => {
        codes.add(row.code);
      })

      planDiff.productsDiff.updated.forEach((row) => {
        if (row.oldValue.qty === row.newValue.qty) return;
        codes.add(row.oldValue.code);
      })
    });

    codes.forEach((code) => {
      result.push({ code })
    })

    return result;
  }

  static async importDiffs(diffs: Parameters<typeof ProductionPlansDAO.importDiffs>[0]) {
    const result = await ProductionPlansDAO.importDiffs(diffs);
    return result.data;
  }

  static async getDiffs(payload: any) {
    const result = await ProductionPlansDAO.getDiffs(payload);

    if (result.ok === false && result.error?.message) {
      Core.showNotification.error(result.error?.message, { duration: 12000 })
    }

    return result.data ?? [];
  }

  static async exportDiffs(diff: Parameters<typeof ProductionPlansDAO.exportDiffs>[0]): Promise<void> {
    const result = await ProductionPlansDAO.exportDiffs(diff);

    if (result.data?.data) {
      const blob = Core.utils.file.base64ToBlob(
        result.data.data,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'Изменения плана'
      );
      Core.utils.file.downloadBlob(blob);
    }
    else {
      Core.showNotification.error('Файл не был получен');
    }
  }

  static async getList(filters: Parameters<typeof ProductionPlansDAO.getList>[0]): Promise<ProductionPlan[]> {
    const response = await ProductionPlansDAO.getList(filters);
    return Core.utils.hydrateArray(response.data, ProductionPlanProduct.fromServer);
  }

  static async getListPaginated(
    filters: Parameters<typeof ProductionPlansDAO.getList>[0]): Promise<{ data: ProductionPlan[], pagination: RpcDataPagination|null }> {
    const plans = await ProductionPlansService.getList(filters);
    return {
      data: plans,
      pagination: null
    }
  }

  static async getActualPlans() {
    return (await ProductionPlansDAO.getActualPlans()).data;
  }

  static async getProductName(
    filters: Parameters<typeof ProductionPlansDAO.getProductName>[0]
  ): Promise<string|null> {
    return (await ProductionPlansDAO.getProductName(filters)).data;
  }

  static async addProducts(
    payload: Parameters<typeof ProductionPlansDAO.addProducts>[0]
  ): Promise<boolean> {
    const response = await ProductionPlansDAO.addProducts(payload)

    if (!response.ok) {
      Core.showNotification.error('Произошла ошибка');
    }

    if (response.data) {
      Core.showNotification.success('Позиция была добавлена/обновлена');
    }

    return response.data;
  }

  static async update(
    position: Parameters<typeof ProductionPlansDAO.updateProductQty>[0]
  ): Promise<boolean> {
    const response = await ProductionPlansDAO.updateProductQty(position);
    if (response.ok) {
      Core.showNotification.success('Успешно обновлен');
    }

    return response.data;
  }

  static async delete(
    position: Parameters<typeof ProductionPlansDAO.deleteProduct>[0]
  ): Promise<boolean> {
    const response = await ProductionPlansDAO.deleteProduct(position);
    if (response.ok) {
      Core.showNotification.success('Успешно удалено');
    }

    return response.data;
  }

  /**
   * История изменений товара в производственных планах.
   * Бэк отдаёт changelog-записи уже в форме записи фида ({parentEntity, time, type, payload}),
   * поэтому гидрируем в FeedRecord — их показывает обычный Feed.
   *
   * @param productCode        код товара
   * @param plannedArrivalDate плановая дата прихода (для истории конкретной ячейки месяца)
   */
  static async getProductLogs(productCode: string, plannedArrivalDate?: string): Promise<FeedRecord[]> {
    const response = await ProductionPlansDAO.getProductLogs({ productCode, plannedArrivalDate });
    return Core.utils.hydrateArray(response.data, FeedRecord.fromServer);
  }

  /**
   * Выгрузить историю изменений товара в xlsx.
   *
   * @param title      заголовок таблицы внутри файла (первая строка xlsx)
   * @param logRecords записи истории — те же, что показаны в фиде
   *
   * TODO: фронт грузит записи (getProductLogs) и шлёт их обратно на бек ради
   *       упаковки в xlsx — лишний round-trip. exportLogs на беке дёргает тот же
   *       ChangelogsService->getList, что и getProductLogs, поэтому мог бы достать
   *       записи сам по productCode/дате и сразу сформировать файл. Передача logs
   *       с фронта — наследие. Когда дойдут руки до бэка — переделать на серверную
   *       выборку, фронт оставит только productCode + период.
   */
  static async exportLogs(title: string, logRecords: FeedRecord[]): Promise<void> {
    const response = await ProductionPlansDAO.exportLogs({ logsTitle: title, logs: logRecords });

    if (response.ok === false) {
      Core.showNotification.error('Ошибка при скачивании файла');
      return;
    }

    // имя файла — из заголовка; точки ломают расширение xlsx, убираем
    const fileName = title.replace(/\./g, '');

    if (!response.data?.data) {
      Core.showNotification.error('Файл не был получен');
      return;
    }

    const blob = Core.utils.file.base64ToBlob(
      response.data.data,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      fileName,
    );
    Core.utils.file.downloadBlob(blob);

    Core.showNotification.success('Файл скачан');
  }

  static async checkProductsExist(
    params: Parameters<typeof ProductionPlansDAO.checkProductsExist>[0]
  ): Promise<Array<any>> {
    const response = await ProductionPlansDAO.checkProductsExist(params);
    if (!response.ok || !response?.data) Core.showNotification.error('Произошла ошибка')

    return response.data;
  }
}

export default ProductionPlansService;
